import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import DoneIcon from "@mui/icons-material/Done";
import { useDispatch, useSelector } from "react-redux";
import {
  StyledTableRow,
  TypographyNormal,
  TypographySmall,
} from "../../../../components/customComponent";
import { bplrtAXIOS } from "../../../../config/axios";
import { instanceSMPAPI } from "../../../../api";
import { editRemark } from "../../../store/actions/bplrt/equipmentAction";
import { logger } from "../../../../helpers/logger";
import { colorStyling } from "../../../../helpers/color";
import { SET_SMP_STATION } from "../../../store/actionTypes/bplrt/smpActionType";
import {
  SET_ERROR_PROMPT_REMARK_DIALOG,
  SET_SUCCESS_PROMPT_REMARK_DIALOG,
} from "../../../store/actionTypes/bplrt/equipmentActionType";
import { handleClosePrompt } from "../../../../store/handlePrompt";
import { useTranslation } from "react-i18next";
import parser from "html-react-parser";

const UpdateRemark = () => {
  const dispatch = useDispatch();

  const { stationDisruptions: stations } = useSelector(
    (state) => state.bplrtSmp
  );

  const {
    isSuccessPromptRemarkDialog: isSuccess,
    successMessageRemarkDialog: successMessage,
    isErrorPromptRemarkDialog: isError,
    errorMessageRemarkDialog: errorMessage,
  } = useSelector((state) => state.bplrtEquip);

  const { t } = useTranslation("bplrtEquipment");

  const [open, setOpen] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [remarkData, setRemarkData] = useState([]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setRemarkData([]);
  };

  const handleOpenConfirmation = () => {
    if (remarkData.length > 0) setOpenConfirmation(true);
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  const handleRemarkField = (e, id) => {
    if (e.target.value.length > 54) {
      e.target.value = e.target.value.substring(0, 54);
      return;
    }

    const objIndex = remarkData.findIndex((obj) => obj.id === id);
    if (objIndex === -1) {
      setRemarkData([
        ...remarkData,
        {
          id,
          comment: e.target.value,
        },
      ]);
    } else {
      let newArr = [...remarkData];
      newArr[objIndex] = {
        id,
        comment: e.target.value,
      };
      setRemarkData(newArr);
    }
  };

  const finishSubmit = (isSuccess) => {
    if (isSuccess) {
      handleClose();
    }
    setUpdating(false);
  };

  const handleSubmit = () => {
    const payload = remarkData;
    setUpdating(true);
    dispatch(editRemark(payload, finishSubmit));
  };

  useEffect(() => {
    const getStations = async () => {
      const access_token = sessionStorage.getItem("access_token");
      setLoading(true);
      try {
        const { data } = await bplrtAXIOS.get(
          instanceSMPAPI + "station?limit=100",
          {
            headers: { authorization: `Bearer ${access_token}` },
          }
        );
        dispatch({ type: SET_SMP_STATION, payload: data.items });
      } catch (error) {
        logger.error(error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      getStations();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Button
        onClick={handleOpen}
        startIcon={<CommentOutlinedIcon fontSize="small" />}
        variant="contained"
        fullWidth
        sx={{ boxShadow: 3 }}
      >
        {t("remarkBtn")}
      </Button>

      <Dialog open={open} maxWidth="md" fullWidth onClose={handleClose}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {t("remarkDialogTitle")}
          <IconButton onClick={handleClose}>
            <CloseIcon fontSize="small" color="inherit" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {isSuccess && (
            <Alert
              sx={{ my: 2 }}
              severity="success"
              variant="filled"
              onClose={() =>
                handleClosePrompt(dispatch, SET_SUCCESS_PROMPT_REMARK_DIALOG)
              }
            >
              {successMessage}
            </Alert>
          )}

          {isError && (
            <Alert
              sx={{ my: 2 }}
              severity="error"
              variant="filled"
              onClose={() =>
                handleClosePrompt(dispatch, SET_ERROR_PROMPT_REMARK_DIALOG)
              }
            >
              {errorMessage}
            </Alert>
          )}

          <TypographySmall>{t("remarkMaxCharText")}</TypographySmall>
          {loading && stations.length === 0 ? (
            <Box
              sx={{ height: 200 }}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              <CircularProgress
                size={20}
                thickness={3}
                sx={{ color: colorStyling.primary }}
              />
              <TypographyNormal
                sx={{ color: colorStyling.primary, mt: 2, fontWeight: 300 }}
              >
                {t("loadingData")}
              </TypographyNormal>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ overflowY: "auto", maxHeight: 500, boxShadow: 3, mt: 1 }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell width="50%">
                      {t("tableHeader.station")}
                    </TableCell>
                    <TableCell>{t("tableHeader.remark")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stations.map((station) => (
                    <StyledTableRow key={station.id}>
                      <TableCell sx={{ py: "8px" }}>
                        {station.code + " - " + station.name}
                      </TableCell>
                      <TableCell sx={{ p: "8px" }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="..."
                          defaultValue={station.comment}
                          inputProps={{ maxLength: 54 }}
                          onChange={(e) => {
                            handleRemarkField(e, station.id);
                          }}
                        />
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button fullWidth variant="outlined" onClick={handleClose}>
            {t("cancelBtn")}
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={handleOpenConfirmation}
            disabled={remarkData.length <= 0 || updating}
          >
            {updating ? <CircularProgress size={24} /> : t("remarkSubmitBtn")}
          </Button>

          <Confirmation
            open={openConfirmation}
            onClose={handleCloseConfirmation}
            handleSubmit={handleSubmit}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UpdateRemark;

const Confirmation = ({ open, onClose, handleSubmit }) => {
  const { t } = useTranslation("bplrtEquipment");

  const handleCallbackSubmit = (event) => {
    event.preventDefault();
    onClose();
    handleSubmit();
  };
  return (
    <Dialog maxWidth="sm" open={open} onClose={onClose}>
      <DialogContent>
        <Box>
          <DialogContentText sx={{ mb: 2 }}>
            {parser(t("remarkConfirmHint"))}
          </DialogContentText>
          <Alert severity="warning" variant="outlined">
            <strong>{t("remarkConfirmAlert")}</strong>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<ClearIcon />}
          fullWidth
          variant="outlined"
          onClick={onClose}
        >
          {t("remarkConfirmNoBtn")}
        </Button>

        <Button
          onClick={handleCallbackSubmit}
          startIcon={<DoneIcon />}
          fullWidth
          variant="contained"
        >
          {t("remarkConfirmYesBtn")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
